import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api/useApiClient';
import { useAuth } from '@/lib/auth/useAuth';
import type {
  AdminProductsResponse,
  CreateCustomerInput,
  Customer,
  CustomerListResponse,
  ResetPasswordInput,
  UpdateCustomerInput,
} from './types';

const customerKeys = {
  all: ['admin-customers'] as const,
  list: (search: string) => [...customerKeys.all, 'list', { search }] as const,
  detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
};

const adminProductKeys = { all: ['admin-products'] as const };

// Assignable product catalogue used by admin customer create/edit. Distinct
// from useProducts() (in features/issues/api.ts), which returns the caller's
// own scoped list.
export function useAdminProducts(opts: { enabled?: boolean } = {}) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: adminProductKeys.all,
    enabled: (opts.enabled ?? true) && Boolean(accessToken),
    // The product catalogue is config-driven — no need to refetch on focus.
    staleTime: Infinity,
    queryFn: () => api.request<AdminProductsResponse>('/admin/products'),
  });
}

// Optional opt-out for non-admin callers that still need to obey rules-of-hooks
// (e.g. IssueListTable always calls useCustomers but only consumes the data
// when the viewer is an admin — pass enabled:false otherwise so the request
// doesn't fire at all).
export function useCustomers(search: string, opts: { enabled?: boolean } = {}) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: customerKeys.list(search),
    enabled: (opts.enabled ?? true) && Boolean(accessToken),
    queryFn: () =>
      api.request<CustomerListResponse>('/admin/customers', {
        query: { search: search || undefined },
      }),
  });
}

export function useCustomer(id: string | undefined) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: customerKeys.detail(id ?? ''),
    enabled: Boolean(id) && Boolean(accessToken),
    queryFn: async () => {
      // No dedicated GET-by-id endpoint yet — derive from the list. Cheap until
      // the customer count grows past a few hundred, at which point add /admin/customers/:id.
      const list = await api.request<CustomerListResponse>('/admin/customers', {
        query: { limit: 200 },
      });
      const found = list.customers.find((c) => c.id === id);
      if (!found) throw new Error('Customer not found');
      return found;
    },
  });
}

export function useCreateCustomer() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      api.request<Customer>('/admin/customers', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useUpdateCustomer(id: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCustomerInput) =>
      api.request<Customer>(`/admin/customers/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useResetPassword(id: string) {
  const api = useApiClient();
  return useMutation({
    mutationFn: (input: ResetPasswordInput) =>
      api.request<void>(`/admin/customers/${id}/reset-password`, {
        method: 'POST',
        body: input,
      }),
  });
}
